import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Building2, Link, Instagram, Clock, Calendar } from 'lucide-react';

const HotspotRequestPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    venue_name: '',
    address: '',
    type: 'bar',
    website: '',
    instagram: '',
    busy_days: '',
    busy_times: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hotspot-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          user_id: 'user_1' // TODO: Replace with actual user ID from context
        })
      });

      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting hotspot request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-espresso to-black flex items-center justify-center p-4">
        <div className="glass-panel-luxury text-center p-8 max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-gold via-gold-deep to-gold-muted flex items-center justify-center gold-glow">
            <MapPin className="text-black" size={40} />
          </div>
          <h2 className="text-3xl font-cinzel gold-text-luxury mb-4">Request Submitted</h2>
          <p className="text-cream text-lg">Thank you. Our team will review this hotspot for NearU.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-espresso to-black pb-24">
      {/* Header */}
      <div className="glass-panel-luxury sticky top-0 z-10 border-b border-gold/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="gold-button-3d p-2 rounded-lg"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-cinzel gold-text-luxury">Request a Hotspot</h1>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto p-6 mt-6">
        <div className="glass-panel-luxury p-8">
          <div className="mb-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-gold via-gold-deep to-gold-muted flex items-center justify-center gold-glow">
              <MapPin className="text-black" size={32} />
            </div>
            <p className="text-cream/70 text-lg">
              Know a venue that should be on NearU? Submit it for our team's review.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Venue Name */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Building2 size={18} className="inline mr-2" />
                Venue Name *
              </label>
              <input
                type="text"
                required
                value={formData.venue_name}
                onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
                placeholder="The Ivy Lounge"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <MapPin size={18} className="inline mr-2" />
                Address *
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
                placeholder="123 Ocean Drive, Miami Beach, FL 33139"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">Venue Type *</label>
              <select
                required
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
              >
                <option value="bar">Bar</option>
                <option value="club">Club</option>
                <option value="lounge">Lounge</option>
                <option value="restaurant">Restaurant</option>
                <option value="rooftop">Rooftop</option>
                <option value="beach_club">Beach Club</option>
                <option value="gym">Gym</option>
                <option value="spa">Spa</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Website */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Link size={18} className="inline mr-2" />
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
                placeholder="https://example.com"
              />
            </div>

            {/* Instagram */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Instagram size={18} className="inline mr-2" />
                Instagram Handle
              </label>
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
                placeholder="@theivylounge"
              />
            </div>

            {/* Busy Days */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Calendar size={18} className="inline mr-2" />
                Busiest Days
              </label>
              <input
                type="text"
                value={formData.busy_days}
                onChange={(e) => setFormData({ ...formData, busy_days: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
                placeholder="Friday, Saturday"
              />
            </div>

            {/* Busy Times */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Clock size={18} className="inline mr-2" />
                Peak Hours
              </label>
              <input
                type="text"
                value={formData.busy_times}
                onChange={(e) => setFormData({ ...formData, busy_times: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
                placeholder="10 PM - 2 AM"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full gold-button-3d py-4 rounded-xl font-cormorant text-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HotspotRequestPage;